import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Server, Link2, CheckCircle, AlertCircle, Loader2, CloudUpload, Unlink } from 'lucide-react';
import { useAppContext, Package, PackageCategory, ServerConnection } from '../../context/AppContext';
const panelColors: Record<string, string> = {
  cpanel: 'text-orange-500',
  plesk: 'text-blue-500',
  sonicpanel: 'text-cyan-500',
  directadmin: 'text-indigo-500',
  cyberpanel: 'text-green-500',
  cloudpanel: 'text-sky-500',
  virtualmin: 'text-violet-500',
  ispconfig: 'text-amber-500',
  centova: 'text-pink-500',
  whmcs: 'text-emerald-500'
};
const panelLabels: Record<string, string> = {
  cpanel: 'cPanel',
  plesk: 'Plesk',
  sonicpanel: 'SonicPanel',
  directadmin: 'DirectAdmin',
  cyberpanel: 'CyberPanel',
  cloudpanel: 'CloudPanel',
  virtualmin: 'Virtualmin',
  ispconfig: 'ISPConfig',
  centova: 'Centova Cast',
  whmcs: 'WHMCS'
};
export function PackageManager() {
  const {
    packages,
    addPackage,
    updatePackage,
    deletePackage,
    serverConnections
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<PackageCategory>('hosting');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<Package | null>(null);
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState<'success' | 'error' | null>(null);
  const filteredPackages = packages.filter(p => p.category === activeTab);
  const connectedServers = serverConnections.filter(s => s.status === 'connected');
  const getServerById = (id: string): ServerConnection | undefined => serverConnections.find(s => s.id === id);
  const handleEdit = (pkg: Package) => {
    setEditingPkg(pkg);
    setSelectedServerId(pkg.serverId || '');
    setProvisionResult(null);
    setIsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      deletePackage(id);
    }
  };
  const openAdd = () => {
    setEditingPkg(null);
    setSelectedServerId('');
    setProvisionResult(null);
    setIsModalOpen(true);
  };
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const featuresStr = formData.get('features') as string;
    const serverPackageName = formData.get('serverPackageName') as string || '';
    const pkgData: Package = {
      id: editingPkg ? editingPkg.id : `pkg_${Date.now()}`,
      category: formData.get('category') as PackageCategory,
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      monthlyPrice: parseFloat(formData.get('monthlyPrice') as string),
      yearlyPrice: parseFloat(formData.get('yearlyPrice') as string),
      features: featuresStr.split('\n').filter(f => f.trim() !== ''),
      highlighted: formData.get('highlighted') === 'on',
      active: formData.get('active') === 'on',
      serverId: selectedServerId || undefined,
      serverPackageName: serverPackageName || undefined,
      provisionStatus: selectedServerId ? 'pending' : 'not-linked'
    };
    if (selectedServerId && serverPackageName) {
      // Simulate provisioning on the server
      setIsProvisioning(true);
      setProvisionResult(null);
      setTimeout(() => {
        const success = Math.random() > 0.15;
        setIsProvisioning(false);
        setProvisionResult(success ? 'success' : 'error');
        pkgData.provisionStatus = success ? 'provisioned' : 'failed';
        if (editingPkg) {
          updatePackage(pkgData.id, pkgData);
        } else {
          addPackage(pkgData);
        }
        // Auto-close on success after a brief delay
        if (success) {
          setTimeout(() => {
            setIsModalOpen(false);
            setEditingPkg(null);
            setProvisionResult(null);
          }, 1500);
        }
      }, 2500);
    } else {
      // Save without provisioning
      if (editingPkg) {
        updatePackage(pkgData.id, pkgData);
      } else {
        addPackage(pkgData);
      }
      setIsModalOpen(false);
      setEditingPkg(null);
    }
  };
  const getProvisionBadge = (pkg: Package) => {
    if (!pkg.serverId) return null;
    const server = getServerById(pkg.serverId);
    if (!server) return null;
    const statusColors = {
      provisioned: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      failed: 'bg-red-500/10 text-red-500 border-red-500/20',
      'not-linked': 'bg-slate-800 text-slate-400 border-slate-700'
    };
    const statusIcons = {
      provisioned: <CheckCircle size={10} data-id="element-825" />,
      pending: <Loader2 size={10} className="animate-spin" data-id="element-826" />,
      failed: <AlertCircle size={10} data-id="element-827" />,
      'not-linked': <Unlink size={10} data-id="element-828" />
    };
    const status = pkg.provisionStatus || 'not-linked';
    return <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-slate-800/50" data-id="element-829">
        <div className="flex items-center gap-2" data-id="element-830">
          <Server size={12} className={panelColors[server.type] || 'text-slate-400'} data-id="element-831" />
          <span className="text-xs text-slate-400 truncate" data-id="element-832">{server.name}</span>
          <span className="text-[10px] text-slate-600" data-id="element-833">
            ({panelLabels[server.type] || server.type})
          </span>
        </div>
        <div className="flex items-center gap-2" data-id="element-834">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${statusColors[status]}`} data-id="element-835">
            {statusIcons[status]}
            {status === 'provisioned' ? 'Provisioned' : status === 'pending' ? 'Pending' : status === 'failed' ? 'Failed' : 'Not Linked'}
          </span>
          {pkg.serverPackageName && <span className="text-[10px] text-slate-500 font-mono truncate" data-id="element-836">
              → {pkg.serverPackageName}
            </span>}
        </div>
      </div>;
  };
  return <div className="space-y-8" data-id="element-837">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-838">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-839">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-840">
            Packages & Services
          </h1>
          <p className="text-slate-400" data-id="element-841">
            Manage pricing plans and link them to server control panels for
            automatic provisioning.
          </p>
        </motion.div>
        <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-842">
          <Plus size={18} data-id="element-843" /> Add Package
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 overflow-x-auto" data-id="element-844">
        {(['hosting', 'radio', 'reseller', 'shoutcast', 'shoutcast-reseller'] as PackageCategory[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === tab ? 'border-fox-500 text-fox-500' : 'border-transparent text-slate-400 hover:text-slate-300'}`} data-id="element-845">
            {tab === 'hosting' ? 'Web Hosting' : tab === 'radio' ? 'Radio Station' : tab === 'reseller' ? 'Reseller' : tab === 'shoutcast' ? 'Shoutcast' : 'Shoutcast Reseller'}
          </button>)}
      </div>

      {/* Package Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-846">
        {filteredPackages.map((pkg, i) => <motion.div key={pkg.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className={`bg-slate-950 border rounded-2xl p-6 relative ${pkg.highlighted ? 'border-fox-500' : 'border-slate-800'} ${!pkg.active ? 'opacity-60' : ''}`} data-id="element-847">
            {!pkg.active && <div className="absolute top-4 right-4 bg-slate-800 text-slate-400 text-xs px-2 py-1 rounded" data-id="element-848">
                Inactive
              </div>}
            {pkg.highlighted && pkg.active && <div className="absolute top-4 right-4 bg-fox-500/20 text-fox-500 text-xs px-2 py-1 rounded font-bold" data-id="element-849">
                Highlighted
              </div>}
            <h3 className="text-xl font-bold text-white mb-1" data-id="element-850">{pkg.name}</h3>
            <p className="text-slate-400 text-sm mb-4 h-10" data-id="element-851">
              {pkg.description}
            </p>
            <div className="mb-2" data-id="element-852">
              <span className="text-2xl font-bold text-white" data-id="element-853">
                ${pkg.monthlyPrice}
              </span>
              <span className="text-slate-400" data-id="element-854">/mo</span>
            </div>

            {getProvisionBadge(pkg)}

            <div className="flex gap-2 mt-5" data-id="element-855">
              <button onClick={() => handleEdit(pkg)} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2" data-id="element-856">
                <Edit2 size={14} data-id="element-857" /> Edit
              </button>
              <button onClick={() => handleDelete(pkg.id)} className="px-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors flex items-center justify-center" data-id="element-858">
                <Trash2 size={16} data-id="element-859" />
              </button>
            </div>
          </motion.div>)}
      </div>

      {/* Modal */}
      <AnimatePresence data-id="element-860">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-861">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-862">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-863">
                <h2 className="text-xl font-bold text-white" data-id="element-864">
                  {editingPkg ? 'Edit Package' : 'Add New Package'}
                </h2>
                <button onClick={() => {
              if (!isProvisioning) {
                setIsModalOpen(false);
                setProvisionResult(null);
              }
            }} className="text-slate-400 hover:text-white" data-id="element-865">
                  <X size={24} data-id="element-866" />
                </button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-6" data-id="element-867">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-868">
                  <div className="space-y-2" data-id="element-869">
                    <label className="text-sm font-medium text-slate-300" data-id="element-870">
                      Package Name *
                    </label>
                    <input name="name" defaultValue={editingPkg?.name} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-871" />
                  </div>
                  <div className="space-y-2" data-id="element-872">
                    <label className="text-sm font-medium text-slate-300" data-id="element-873">
                      Category *
                    </label>
                    <select name="category" defaultValue={editingPkg?.category || activeTab} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-874">
                      <option value="hosting" data-id="element-875">Web Hosting</option>
                      <option value="radio" data-id="element-876">Radio Station</option>
                      <option value="reseller" data-id="element-877">Reseller Account</option>
                      <option value="shoutcast" data-id="element-878">Shoutcast</option>
                      <option value="shoutcast-reseller" data-id="element-879">
                        Shoutcast Reseller
                      </option>
                    </select>
                  </div>
                  <div className="space-y-2 md:col-span-2" data-id="element-880">
                    <label className="text-sm font-medium text-slate-300" data-id="element-881">
                      Description *
                    </label>
                    <input name="description" defaultValue={editingPkg?.description} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-882" />
                  </div>
                  <div className="space-y-2" data-id="element-883">
                    <label className="text-sm font-medium text-slate-300" data-id="element-884">
                      Monthly Price ($) *
                    </label>
                    <input name="monthlyPrice" type="number" step="0.01" defaultValue={editingPkg?.monthlyPrice} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-885" />
                  </div>
                  <div className="space-y-2" data-id="element-886">
                    <label className="text-sm font-medium text-slate-300" data-id="element-887">
                      Yearly Price ($) *
                    </label>
                    <input name="yearlyPrice" type="number" step="0.01" defaultValue={editingPkg?.yearlyPrice} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-888" />
                  </div>
                  <div className="space-y-2 md:col-span-2" data-id="element-889">
                    <label className="text-sm font-medium text-slate-300" data-id="element-890">
                      Features (One per line) *
                    </label>
                    <textarea name="features" rows={4} defaultValue={editingPkg?.features.join('\n')} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none resize-none" placeholder="10 GB Storage&#10;Unmetered Bandwidth" data-id="element-891" />
                  </div>
                </div>

                {/* Server Linking Section */}
                <div className="border border-slate-800 rounded-xl overflow-hidden" data-id="element-892">
                  <div className="bg-slate-950 px-5 py-4 border-b border-slate-800" data-id="element-893">
                    <div className="flex items-center gap-2" data-id="element-894">
                      <Link2 size={16} className="text-fox-500" data-id="element-895" />
                      <h3 className="text-white font-bold text-sm" data-id="element-896">
                        Server Provisioning
                      </h3>
                    </div>
                    <p className="text-slate-500 text-xs mt-1" data-id="element-897">
                      Link this package to a server so it auto-creates on the
                      control panel when saved.
                    </p>
                  </div>
                  <div className="p-5 space-y-4 bg-slate-900/50" data-id="element-898">
                    <div className="space-y-2" data-id="element-899">
                      <label className="text-sm font-medium text-slate-300" data-id="element-900">
                        Linked Server
                      </label>
                      {connectedServers.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-center" data-id="element-901">
                          <Server size={20} className="mx-auto text-slate-600 mb-2" data-id="element-902" />
                          <p className="text-slate-500 text-sm" data-id="element-903">
                            No connected servers available.
                          </p>
                          <p className="text-slate-600 text-xs mt-1" data-id="element-904">
                            Add a server in Server Integrations first.
                          </p>
                        </div> : <div className="grid grid-cols-1 gap-2" data-id="element-905">
                          {/* None option */}
                          <button type="button" onClick={() => setSelectedServerId('')} className={`p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${selectedServerId === '' ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-906">
                            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center" data-id="element-907">
                              <Unlink size={14} className="text-slate-500" data-id="element-908" />
                            </div>
                            <div data-id="element-909">
                              <span className="text-white text-sm font-medium" data-id="element-910">
                                No Server Link
                              </span>
                              <p className="text-slate-500 text-[11px]" data-id="element-911">
                                Package won't be provisioned on any server
                              </p>
                            </div>
                          </button>

                          {connectedServers.map(server => <button key={server.id} type="button" onClick={() => setSelectedServerId(server.id)} className={`p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${selectedServerId === server.id ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-912">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800`} data-id="element-913">
                                <Server size={14} className={panelColors[server.type] || 'text-slate-400'} data-id="element-914" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-915">
                                <div className="flex items-center gap-2" data-id="element-916">
                                  <span className="text-white text-sm font-medium truncate" data-id="element-917">
                                    {server.name}
                                  </span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${panelColors[server.type] ? panelColors[server.type] + ' bg-slate-900' : 'text-slate-400 bg-slate-900'}`} data-id="element-918">
                                    {panelLabels[server.type] || server.type}
                                  </span>
                                </div>
                                <p className="text-slate-500 text-[11px] font-mono truncate" data-id="element-919">
                                  {server.hostname}:{server.port}
                                </p>
                              </div>
                              <div className="flex items-center gap-1" data-id="element-920">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-921" />
                              </div>
                            </button>)}
                        </div>}
                    </div>

                    {selectedServerId && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} className="space-y-2" data-id="element-922">
                        <label className="text-sm font-medium text-slate-300" data-id="element-923">
                          Server Package Name *
                        </label>
                        <input name="serverPackageName" defaultValue={editingPkg?.serverPackageName} required={!!selectedServerId} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder={`e.g. ${(() => {
                    const server = getServerById(selectedServerId);
                    if (!server) return 'package_starter';
                    switch (server.type) {
                      case 'cpanel':
                        return 'shared_starter_10gb';
                      case 'plesk':
                        return 'plesk_plan_basic';
                      case 'sonicpanel':
                        return 'radio_128kbps_50';
                      case 'directadmin':
                        return 'da_hosting_basic';
                      case 'centova':
                        return 'shoutcast_starter';
                      case 'whmcs':
                        return 'product_group_hosting';
                      default:
                        return 'package_name';
                    }
                  })()}`} data-id="element-924" />
                        <p className="text-slate-600 text-xs" data-id="element-925">
                          This must match the package/plan name on the{' '}
                          {panelLabels[getServerById(selectedServerId)?.type || ''] || 'server'}{' '}
                          control panel, or a new one will be created.
                        </p>
                      </motion.div>}
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-4" data-id="element-926">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-927">
                    <input type="checkbox" name="highlighted" defaultChecked={editingPkg?.highlighted} className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500" data-id="element-928" />
                    <span className="text-white" data-id="element-929">
                      Highlight as "Most Popular"
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-930">
                    <input type="checkbox" name="active" defaultChecked={editingPkg ? editingPkg.active : true} className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500" data-id="element-931" />
                    <span className="text-white" data-id="element-932">
                      Active (Visible on pricing page)
                    </span>
                  </label>
                </div>

                {/* Provisioning Result */}
                <AnimatePresence data-id="element-933">
                  {(isProvisioning || provisionResult) && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} className={`p-4 rounded-xl border flex items-center gap-3 ${isProvisioning ? 'bg-fox-500/10 border-fox-500/20 text-fox-400' : provisionResult === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-id="element-934">
                      {isProvisioning ? <>
                          <Loader2 size={18} className="animate-spin" data-id="element-935" />
                          <div data-id="element-936">
                            <p className="font-medium text-sm" data-id="element-937">
                              Provisioning on server...
                            </p>
                            <p className="text-xs opacity-75" data-id="element-938">
                              Creating package on{' '}
                              {getServerById(selectedServerId)?.name}. This may
                              take a moment.
                            </p>
                          </div>
                        </> : provisionResult === 'success' ? <>
                          <CheckCircle size={18} data-id="element-939" />
                          <div data-id="element-940">
                            <p className="font-medium text-sm" data-id="element-941">
                              Package provisioned successfully!
                            </p>
                            <p className="text-xs opacity-75" data-id="element-942">
                              The package has been created on{' '}
                              {getServerById(selectedServerId)?.name} and is
                              ready for use.
                            </p>
                          </div>
                        </> : <>
                          <AlertCircle size={18} data-id="element-943" />
                          <div data-id="element-944">
                            <p className="font-medium text-sm" data-id="element-945">
                              Provisioning failed
                            </p>
                            <p className="text-xs opacity-75" data-id="element-946">
                              Could not create the package on the server. The
                              package was saved locally — you can retry
                              provisioning later.
                            </p>
                          </div>
                        </>}
                    </motion.div>}
                </AnimatePresence>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-947">
                  <button type="button" onClick={() => {
                if (!isProvisioning) {
                  setIsModalOpen(false);
                  setProvisionResult(null);
                }
              }} disabled={isProvisioning} className="px-4 py-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50" data-id="element-948">
                    Cancel
                  </button>
                  <button type="submit" disabled={isProvisioning} className="bg-fox-600 hover:bg-fox-500 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2" data-id="element-949">
                    {isProvisioning ? <Loader2 size={16} className="animate-spin" data-id="element-950" /> : selectedServerId ? <CloudUpload size={16} data-id="element-951" /> : null}
                    {isProvisioning ? 'Provisioning...' : selectedServerId ? 'Save & Provision' : 'Save Package'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}